import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, User, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { PositionInterviewingMode } from '@/types/server/enums.types';
import { loginCandidate, verifyCandidateOTP } from '@/api/auth.api';
import { checkCandidateEligibility } from '@/api/positions.api';
import { logError } from '@/utils/logger.utils';

export const CandidateLoginPage = () => {
	const [email, setEmail] = useState('');
	const [otp, setOtp] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [otpSent, setOtpSent] = useState(false);
	const [resendCountdown, setResendCountdown] = useState(0);
	const [canResend, setCanResend] = useState(false);
	const navigate = useNavigate();
	const { positionId } = useParams<{ positionId: string }>();
	const {
		positionStatus,
		setCandidateAuthToken,
		isAdminAuthenticated,
		positionAdminAuthToken,
		logoutAdmin,
	} = useAuth();

	// Handle resend countdown timer
	useEffect(() => {
		let timer: NodeJS.Timeout;
		if (resendCountdown > 0) {
			timer = setTimeout(() => {
				setResendCountdown(resendCountdown - 1);
			}, 1000);
		} else if (resendCountdown === 0 && otpSent) {
			setCanResend(true);
		}
		return () => clearTimeout(timer);
	}, [resendCountdown, otpSent]);

	const startResendCountdown = () => {
		setResendCountdown(30);
		setCanResend(false);
	};

	const handleSendOtp = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!positionId || !email) return;

		setIsLoading(true);

		try {
			const response = await loginCandidate(
				positionId,
				email,
				positionAdminAuthToken || undefined
			);

			// Handle HTTP error codes
			if (
				response.httpResponseCode === 500 ||
				response.httpResponseCode === 400 ||
				response.identificationCode === 'loginCandidate_candidateNotAllowed' ||
				response.identificationCode === 'loginCandidate_candidateNotInvited' ||
				response.identificationCode ===
					'loginCandidate_newCandidatesLimitExceeded' ||
				response.identificationCode === 'loginCandidate_otpLimitExceeded'
			) {
				toast.error(response.message);
				return;
			}

			// Handle identification codes
			if (response.identificationCode === 'loginCandidate_positionNotFound') {
				toast.error(response.message);
				navigate(`/${positionId}/not-found`);
				return;
			}

			if (
				response.identificationCode === 'loginCandidate_invalidAdminAuthToken'
			) {
				toast.error(response.message);
				logoutAdmin();
				setTimeout(() => {
					window.location.reload();
				}, 4000);
				return;
			}

			// Handle successful response (200)
			if (response.httpResponseCode === 200) {
				toast.success(response.message);
				setOtpSent(true);
				startResendCountdown();
			}
		} catch (error) {
			logError(error);
			toast.error(
				'Failed to send OTP. Please refresh the page and try again. If you think this is an error, please contact support.'
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleResendOtp = async () => {
		if (!positionId || !email || !canResend) return;

		setIsLoading(true);

		try {
			const response = await loginCandidate(
				positionId,
				email,
				positionAdminAuthToken || undefined
			);

			// Handle HTTP error codes
			if (
				response.httpResponseCode === 500 ||
				response.httpResponseCode === 400 ||
				response.identificationCode === 'loginCandidate_candidateNotAllowed' ||
				response.identificationCode === 'loginCandidate_candidateNotInvited' ||
				response.identificationCode ===
					'loginCandidate_newCandidatesLimitExceeded' ||
				response.identificationCode === 'loginCandidate_otpLimitExceeded'
			) {
				toast.error(response.message);
				return;
			}

			// Handle identification codes
			if (response.identificationCode === 'loginCandidate_positionNotFound') {
				toast.error(response.message);
				navigate(`/${positionId}/not-found`);
				return;
			}

			if (
				response.identificationCode === 'loginCandidate_invalidAdminAuthToken'
			) {
				toast.error(response.message);
				setTimeout(() => {
					window.location.reload();
				}, 4000);
				return;
			}

			// Handle successful response (200)
			if (response.httpResponseCode === 200) {
				toast.success(response.message);
				startResendCountdown();
			}
		} catch (error) {
			console.error('Error resending OTP:', error);
			toast.error('Failed to resend OTP. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleVerifyOtp = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!positionId || !otp || !email) return;

		setIsLoading(true);

		try {
			// Step 1: Verify OTP
			const otpResponse = await verifyCandidateOTP(email, otp);

			// Handle HTTP error codes (500, 404, 403)
			if (
				otpResponse.httpResponseCode === 500 ||
				otpResponse.httpResponseCode === 404 ||
				otpResponse.httpResponseCode === 403
			) {
				toast.error(otpResponse.message);
				return;
			}

			// Handle successful OTP verification (200)
			if (otpResponse.httpResponseCode === 200 && otpResponse.data) {
				// Store candidate token and update auth state
				setCandidateAuthToken(otpResponse.data.candidateAuthToken);

				// Step 2: Check candidate eligibility
				const eligibilityResponse = await checkCandidateEligibility(
					positionId,
					otpResponse.data.candidateAuthToken,
					positionAdminAuthToken || undefined
				);

				// Handle eligibility check errors
				if (
					eligibilityResponse.identificationCode ===
						'checkCandidateEligibility_positionNotFound' ||
					eligibilityResponse.identificationCode ===
						'checkCandidateEligibility_positionInactive'
				) {
					toast.error(eligibilityResponse.message);
					navigate(`/${positionId}/not-found`);
					return;
				}

				if (
					eligibilityResponse.identificationCode ===
					'checkCandidateEligibility_invalidAdminAuthToken'
				) {
					toast.error(eligibilityResponse.message);
					logoutAdmin();
					setTimeout(() => {
						window.location.reload();
					}, 4000);
					return;
				}

				if (
					eligibilityResponse.identificationCode ===
						'checkCandidateEligibility_invalidCandidateToken' ||
					eligibilityResponse.identificationCode ===
						'checkCandidateEligibility_candidateNotFound'
				) {
					toast.error(eligibilityResponse.message);
					setTimeout(() => {
						window.location.reload();
					}, 4000);
					return;
				}

				if (
					eligibilityResponse.identificationCode ===
						'checkCandidateEligibility_alreadyInterviewed' ||
					eligibilityResponse.identificationCode ===
						'checkCandidateEligibility_notInvited'
				) {
					toast.error(eligibilityResponse.message);
					return;
				}

				// Handle successful eligibility check
				if (
					eligibilityResponse.identificationCode ===
					'checkCandidateEligibility_eligible'
				) {
					navigate(`/${positionId}/lobby`);
					return;
				}

				// Fallback for unexpected response
				throw new Error('Unexpected response from server. Please try again.');
			}
		} catch (error) {
			logError(error);
			toast.error(
				'Failed to verify OTP. Please try again. If you think this is an error, please contact support.'
			);
		} finally {
			setIsLoading(false);
		}
	};

	const showAdminOption =
		positionStatus?.interviewingMode === PositionInterviewingMode.offline &&
		!isAdminAuthenticated;

	return (
		<div className="min-h-screen bg-blue-50 flex items-center justify-center px-4">
			<Card className="w-full max-w-md shadow-lg">
				<CardHeader className="text-center">
					<div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
						<User className="w-6 h-6 text-green-600" />
					</div>
					<CardTitle className="text-2xl font-semibold">
						Candidate Login
					</CardTitle>
					<CardDescription>
						{positionStatus?.title ? (
							<>
								Join the interview for "<strong>{positionStatus.title}</strong>"
							</>
						) : (
							'Enter your email to join the interview'
						)}
					</CardDescription>
				</CardHeader>

				<CardContent>
					{!otpSent ? (
						<form onSubmit={handleSendOtp} className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="email">Email Address</Label>
								<Input
									id="email"
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="candidate@example.com"
									required
								/>
							</div>

							<Button type="submit" className="w-full" disabled={isLoading}>
								{isLoading ? (
									<>
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										Sending OTP...
									</>
								) : (
									<>
										<Mail className="mr-2 h-4 w-4" />
										Send OTP
									</>
								)}
							</Button>
						</form>
					) : (
						<form onSubmit={handleVerifyOtp} className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="otp">Enter OTP</Label>
								<Input
									id="otp"
									type="text"
									value={otp}
									onChange={(e) => setOtp(e.target.value)}
									placeholder="Enter 6-digit OTP"
									maxLength={6}
									required
								/>
								<p className="text-sm text-gray-600">
									OTP sent to <strong>{email}</strong>
								</p>
							</div>

							<Button type="submit" className="w-full" disabled={isLoading}>
								{isLoading ? (
									<>
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										Verifying...
									</>
								) : (
									'Verify OTP & Login'
								)}
							</Button>

							<div className="grid grid-cols-2 gap-3">
								<Button
									type="button"
									variant="outline"
									className="w-full"
									onClick={() => {
										setOtpSent(false);
										setOtp('');
										setResendCountdown(0);
										setCanResend(false);
									}}
								>
									Change Email
								</Button>

								<Button
									type="button"
									variant="outline"
									className="w-full"
									onClick={handleResendOtp}
									disabled={!canResend || isLoading}
								>
									{resendCountdown > 0 ? (
										<>
											<Clock className="mr-2 h-4 w-4" />
											Resend ({resendCountdown}s)
										</>
									) : (
										<>
											<Mail className="mr-2 h-4 w-4" />
											Resend OTP
										</>
									)}
								</Button>
							</div>
						</form>
					)}

					<div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
						{showAdminOption && (
							<div className="text-center">
								<p className="text-sm text-gray-600">
									Are you an admin?{' '}
									<button
										onClick={() => navigate(`/${positionId}/login/admin`)}
										className="text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
									>
										Login as Admin
									</button>
								</p>
							</div>
						)}
						{/* <div className="flex justify-center">
							<LogoutButton type="admin" variant="ghost" />
						</div> */}
					</div>
				</CardContent>
			</Card>
		</div>
	);
};
